
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { supabase } from '../../lib/supabase';

interface PageContent {
  [key: string]: string;
}

export default function About() {
  const { t } = useTranslation();
  const [content, setContent] = useState<PageContent>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPageContent();
  }, []);

  const loadPageContent = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('page_content')
        .select('*')
        .eq('page_name', 'about');

      if (error) throw error;

      const contentMap: PageContent = {};
      data?.forEach((item) => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error loading page content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-[#FCD34D] animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img 
            src={content.hero_image || "https://public.readdy.ai/ai/img_res/a4302e17298e785f0a6052c17b08f370.jpg"}
            alt="About Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
            {content.hero_title || 'Hakkımızda'}
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            {content.hero_subtitle || '2009\'dan beri otomotiv yenileme alanında mükemmellik'}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {content.story_title || 'Hikayemiz'}
              </h2>
              <div className="text-gray-300 text-lg space-y-4 whitespace-pre-line">
                {content.story_text || '2009 yılında kurulan Auto Detailing Pro, basit bir misyonla başladı: lüks araç sahiplerine en yüksek kalitede otomotiv yenileme ve detaylı temizlik hizmetleri sunmak.\n\nYıllar içinde, küçük bir garajdan bölgedeki en güvenilir otomotiv yenileme merkezlerinden birine dönüştük. Başarımız, kaliteye olan bağlılığımıza, müşteri memnuniyetine odaklanmamıza ve zanaatımıza olan tutkumuza dayanmaktadır.\n\nBugün, deneyimli profesyonellerden oluşan bir ekiple gurur duyuyoruz ve her aracı sanat eseri gibi ele alıyoruz. Geleneksel teknikleri modern yeniliklerle birleştirerek, müşterilerimizin beklentilerini sürekli olarak aşan sonuçlar sunuyoruz.'}
              </div>
            </div>
            <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-700 shadow-xl">
              <img 
                src={content.story_image || "https://readdy.ai/api/search-image?query=modern%20automotive%20detailing%20workshop%20interior%20showing%20professional%20equipment%20and%20clean%20organized%20workspace%20with%20yellow%20accent%20lighting%20and%20dark%20background%20representing%20quality%20and%20professionalism&width=800&height=600&seq=aboutstory1&orientation=landscape"}
                alt="Our Workshop"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#1F1F1F] p-10 rounded-lg border border-gray-700 shadow-xl">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg mb-6">
                <i className="ri-target-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">
                {content.mission_title || 'Misyonumuz'}
              </h3>
              <p className="text-gray-300 text-lg whitespace-pre-line">
                {content.mission_text || 'Müşterilerimizin beklentilerini aşan olağanüstü otomotiv yenileme ve detaylı temizlik hizmetleri sunmak. En son teknikleri, premium ürünleri ve titiz bir detaylara dikkat yaklaşımını kullanarak her aracın en iyi şekilde görünmesini ve hissetmesini sağlamaya kendimizi adadık.'}
              </p>
            </div>
            
            <div className="bg-[#1F1F1F] p-10 rounded-lg border border-gray-700 shadow-xl">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg mb-6">
                <i className="ri-eye-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">
                {content.vision_title || 'Vizyonumuz'}
              </h3>
              <p className="text-gray-300 text-lg whitespace-pre-line">
                {content.vision_text || 'Zanaatkarlığımız, yenilikçiliğimiz ve kaliteye olan sarsılmaz bağlılığımızla tanınan, önde gelen otomotiv yenileme ve detaylı temizlik hizmet sağlayıcısı olmak. Sektörde yeni standartlar belirlemeyi ve müşterilerimize olağanüstü sonuçlar sunmaya devam etmeyi hedefliyoruz.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Değerlerimiz
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Yaptığımız her işte bizi yönlendiren temel ilkeler
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D]/20 rounded-full mx-auto mb-6 shadow-lg">
                <i className="ri-star-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Mükemmellik
              </h3>
              <p className="text-gray-300">
                Her projede en yüksek standartları korumaya çalışıyoruz
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D]/20 rounded-full mx-auto mb-6 shadow-lg">
                <i className="ri-shield-check-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Dürüstlük
              </h3>
              <p className="text-gray-300">
                Tüm etkileşimlerimizde şeffaflık ve dürüstlük
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D]/20 rounded-full mx-auto mb-6 shadow-lg">
                <i className="ri-lightbulb-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Yenilikçilik
              </h3>
              <p className="text-gray-300">
                Daha iyi sonuçlar için sürekli gelişme ve uyum sağlama
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D]/20 rounded-full mx-auto mb-6 shadow-lg">
                <i className="ri-heart-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Tutku
              </h3>
              <p className="text-gray-300">
                Yaptığımız işe ve müşterilerimize gerçek sevgi
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#1F1F1F]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2">
                15+
              </div>
              <div className="text-gray-300 text-lg">
                Yıllık Deneyim
              </div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2">
                500+
              </div>
              <div className="text-gray-300 text-lg">
                Mutlu Müşteri
              </div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2">
                11
              </div>
              <div className="text-gray-300 text-lg">
                Hizmet Sayısı
              </div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2">
                100%
              </div>
              <div className="text-gray-300 text-lg">
                Müşteri Memnuniyeti
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
